import React, { useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { PiMapPinArea } from 'react-icons/pi'
import { FaRegCircleCheck } from 'react-icons/fa6'
import { IoWarning } from 'react-icons/io5'
import { OrderContext } from '../../context/order.context'
import './contactform.styles.scss'

const createDefaultValues = () => ({
  name: '',
  email: '',
  message: '',
  status: 'no',
  del: 'no',
  created_at: new Date(),
  updated_at: null,
})

const isFilled = (value) => typeof value === 'string' && value.trim().length > 0

const ContactForm = ({ company = {} }) => {
  const { address1, address2, phone, email } = company || {}
  const { addInquiry } = useContext(OrderContext)

  const [inquire, setInquire] = useState(false)
  const [inquiryText, setInquiryText] = useState('')
  const [formFields, setFormFields] = useState(createDefaultValues)
  const [submitting, setSubmitting] = useState(false)

  const openForm = () => {
    setInquire(true)
    setInquiryText('')
  }

  const closeForm = () => {
    setInquire(false)
    setInquiryText('')
  }

  const startAnother = () => {
    setInquiryText('')
    setFormFields(createDefaultValues())
    setInquire(true)
  }

  const changeFieldText = (event) => {
    const { name, value } = event.target
    setFormFields((prev) => ({ ...prev, [name]: value }))
  }

  const submitInquiry = async (event) => {
    event.preventDefault()

    if (!isFilled(formFields.name) || !isFilled(formFields.email) || !isFilled(formFields.message)) {
      setInquiryText('x')
      return
    }

    if (formFields.message.trim().length < 10) {
      setInquiryText('short')
      return
    }

    setSubmitting(true)

    const payload = {
      name: formFields.name.trim(),
      email: formFields.email.trim(),
      message: formFields.message.trim(),
      status: 'no',
      del: 'no',
      inq_response:
        'Thank you for contacting us. Our support team will reach out to you in less than 24hrs.',
      created_at: new Date(),
      updated_at: null,
    }

    try {
      await addInquiry(payload)
      setInquiryText('y')
      setInquire(false)
      setFormFields(createDefaultValues())
    } catch (error) {
      console.error('Inquiry submit failed:', error)
      setInquiryText('x')
    } finally {
      setSubmitting(false)
    }
  }

  const formVisible = inquiryText !== 'y'

  return (
    <section id="contact" className="inquire-v2">
      <div className="inquire-v2__edge-wrap" aria-hidden="true">
        <div className="inquire-v2__edge">
          <svg viewBox="0 0 1440 160" preserveAspectRatio="none">
            <path
              d="M0 108
              C160 118 280 96 400 78
              C560 52 700 34 860 48
              C1000 60 1120 28 1260 18
              C1340 12 1400 8 1440 14
              L1440 160 L0 160 Z"
            />
          </svg>
        </div>
      </div>

      <div className="inquire-v2__surface">
        <div className="inquire-v2__glow inquire-v2__glow--a" aria-hidden="true" />
        <div className="inquire-v2__glow inquire-v2__glow--b" aria-hidden="true" />
        <div className="inquire-v2__glow inquire-v2__glow--c" aria-hidden="true" />

        <div className="inquire-v2__inner">
          <div className="inquire-v2__copy">
            <p className="inquire-v2__eyebrow">Say hello</p>
            <h2>
              <span className="inquire-v2__script">Wanna talk</span>
              <span className="inquire-v2__solid">to Sandy?</span>
            </h2>

            {!inquire && inquiryText !== 'y' ? (
              <button type="button" className="contact-us-btn" onClick={openForm}>
                Contact Us
              </button>
            ) : null}

            <p className={`inquire-v2__lead${inquire || inquiryText === 'y' ? ' inquire-v2__lead--open' : ''}`}>
              Questions about flavors, dates, or a custom bake? Drop a note and we&apos;ll get back
              soon.
            </p>

            {inquiryText === 'y' ? (
              <div className="inquire-v2__success" role="status">
                <FaRegCircleCheck size={28} aria-hidden="true" />
                <h3>Message received</h3>
                <p>
                  Thanks for reaching out. We&apos;ll review your note and reply within 24 hours.
                </p>
                <div className="inquire-v2__success-actions">
                  <button type="button" onClick={startAnother}>
                    Send another
                  </button>
                  <Link to="/book">Book a bake</Link>
                </div>
              </div>
            ) : null}

            {inquiryText === 'x' ? (
              <p className="inquire-v2__status inquire-v2__status--err" role="alert">
                <IoWarning size={16} aria-hidden="true" />
                Something went wrong. Please try again.
              </p>
            ) : null}

            {inquiryText === 'short' ? (
              <p className="inquire-v2__status inquire-v2__status--err" role="alert">
                <IoWarning size={16} aria-hidden="true" />
                Please add a bit more detail (at least a sentence).
              </p>
            ) : null}

            {formVisible ? (
              <form
                className={`inquire-v2__form${inquire ? ' inquire-v2__form--open' : ''}`}
                onSubmit={submitInquiry}
                noValidate
              >
                <label className="inquire-v2__field">
                  <span>Your name</span>
                  <input
                    name="name"
                    type="text"
                    value={formFields.name}
                    onChange={changeFieldText}
                    placeholder="Alex Baker"
                    autoComplete="name"
                    required
                  />
                </label>

                <label className="inquire-v2__field">
                  <span>Email</span>
                  <input
                    name="email"
                    type="email"
                    value={formFields.email}
                    onChange={changeFieldText}
                    placeholder="you@email.com"
                    autoComplete="email"
                    required
                  />
                </label>

                <label className="inquire-v2__field inquire-v2__field--area">
                  <span>Message</span>
                  <textarea
                    name="message"
                    rows={4}
                    value={formFields.message}
                    onChange={changeFieldText}
                    placeholder="Tell us about your event or question..."
                    required
                  />
                </label>

                <div className="inquire-v2__actions">
                  <button type="button" className="inquire-v2__cancel" onClick={closeForm}>
                    Cancel
                  </button>
                  <button type="submit" className="inquire-v2__submit" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send inquiry'}
                  </button>
                </div>
              </form>
            ) : null}
          </div>

          <aside className={`inquire-v2__aside${inquire || inquiryText === 'y' ? ' inquire-v2__aside--open' : ''}`}>
            <div className="inquire-v2__info">
              <CalendarDaysIcon className="inquire-v2__icon" aria-hidden="true" />
              <div>
                <h3>Response time</h3>
                <p>
                  We usually reply within 24 hours. For timed events, book early so we can hold your
                  date.
                </p>
              </div>
            </div>

            <div className="inquire-v2__info">
              <PiMapPinArea className="inquire-v2__icon" aria-hidden="true" />
              <div>
                <h3>Find us</h3>
                <p>
                  {address1 ? (
                    <>
                      {address1}
                      {address2 ? (
                        <>
                          <br />
                          {address2}
                        </>
                      ) : null}
                    </>
                  ) : (
                    'Location details coming soon.'
                  )}
                  {phone ? (
                    <>
                      <br />
                      <a href={`tel:${phone}`}>{phone}</a>
                    </>
                  ) : null}
                  {email ? (
                    <>
                      <br />
                      <a href={`mailto:${email}`}>{email}</a>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
